/**
 * Phase 9 — Creator Analytics MVP page.
 * Read-only summary of creator's Kickbacks, claims, redemptions, utilities,
 * plan usage, and attention items. Sourced from GET /api/creator/analytics/summary.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart3, Folder, Link2, Gift, Package, CreditCard, AlertTriangle, Clock,
} from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardStyle = {
  background: theme.colors.bg.card,
  border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card,
  padding: 20,
};

const StatRow = ({ label, value, testid }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between',
    padding: '6px 0', borderBottom: `1px dashed ${theme.colors.borders}` }}>
    <span style={{ fontSize: 12, color: theme.colors.text.muted }}>{label}</span>
    <span data-testid={testid} style={{ fontSize: 14, fontWeight: 700,
      color: theme.colors.text.primary }}>
      {value === null || value === undefined ? '—' : value}
    </span>
  </div>
);

const SectionCard = ({ icon: Icon, title, children, testid }) => (
  <div data-testid={testid} style={cardStyle}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
      <Icon size={18} color={theme.colors.primary} />
      <div style={{ fontWeight: 800, fontSize: 15, color: theme.colors.text.primary }}>
        {title}
      </div>
    </div>
    {children}
  </div>
);

export default function CreatorAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/creator/analytics/summary`, auth);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load analytics');
    } finally { setLoading(false); }
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div data-testid="analytics-loading" style={{ padding: 40,
        color: theme.colors.text.muted }}>Loading analytics…</div>
    );
  }

  if (!data) {
    return (
      <div data-testid="analytics-empty" style={{ padding: 40,
        color: theme.colors.text.muted }}>No analytics available.</div>
    );
  }

  const p = data.portfolios || {};
  const c = data.claims || {};
  const r = data.redemptions || {};
  const u = data.utilities || {};
  const pl = data.plan || {};
  const a = data.attention || {};

  return (
    <div data-testid="creator-analytics-page" style={{ padding: 40 }}>
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <BarChart3 size={24} color={theme.colors.primary} />
          <h1 style={{ fontSize: 26, fontWeight: 700, color: theme.colors.text.primary,
            fontFamily: theme.fonts.heading, margin: 0 }}>Creator Analytics</h1>
        </div>
        <p style={{ fontSize: 14, color: theme.colors.text.secondary, marginTop: 6 }}>
          Track Kickback activity, claim engagement, utility usage, and items
          that may need attention.
        </p>
      </div>

      <div style={{ display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>

        <SectionCard icon={Folder} title="Kickbacks" testid="analytics-portfolios-card">
          <StatRow label="Total" value={p.total} testid="analytics-portfolios-total" />
          <StatRow label="Active" value={p.active} testid="analytics-portfolios-active" />
          <StatRow label="Draft" value={p.draft} testid="analytics-portfolios-draft" />
          <StatRow label="Live" value={p.live} testid="analytics-portfolios-live" />
          <StatRow label="Ready to Launch" value={p.ready_to_launch}
            testid="analytics-portfolios-ready" />
        </SectionCard>

        <SectionCard icon={Link2} title="Claims" testid="analytics-claims-card">
          <StatRow label="Total claim links" value={c.total}
            testid="analytics-claims-total" />
          <StatRow label="Active" value={c.active}
            testid="analytics-claims-active" />
          <StatRow label="Exhausted" value={c.exhausted}
            testid="analytics-claims-exhausted" />
          <StatRow label="Paused" value={c.paused}
            testid="analytics-claims-paused" />
          <StatRow label="Revoked" value={c.revoked}
            testid="analytics-claims-revoked" />
        </SectionCard>

        <SectionCard icon={Gift} title="Redemptions" testid="analytics-redemptions-card">
          <StatRow label="Total redemptions" value={r.total}
            testid="analytics-redemptions-total" />
          <StatRow label="This month" value={r.this_month}
            testid="analytics-redemptions-month" />
          <StatRow label="Unique holders" value={r.unique_holders}
            testid="analytics-redemptions-unique" />
        </SectionCard>

        <SectionCard icon={Package} title="Utilities" testid="analytics-utilities-card">
          <StatRow label="Attached utilities" value={u.attached_total}
            testid="analytics-utilities-total" />
          <StatRow label="Active" value={u.active}
            testid="analytics-utilities-active" />
          <StatRow label="Inactive" value={u.inactive}
            testid="analytics-utilities-inactive" />
          <StatRow label="Detached" value={u.detached}
            testid="analytics-utilities-detached" />
          {(u.most_attached_types || []).length > 0 && (
            <div style={{ marginTop: 8, fontSize: 11, color: theme.colors.text.muted }}>
              Top types: {(u.most_attached_types || []).map(t =>
                `${t.utility_type} (${t.count})`).join(', ')}
            </div>
          )}
        </SectionCard>

        <SectionCard icon={CreditCard} title="Plan Usage" testid="analytics-plan-card">
          <StatRow label="Current plan" value={pl.current_plan_name || 'Not synced'}
            testid="analytics-plan-name" />
          <StatRow label="Active Kickbacks used"
            value={pl.active_kickback_used ?? '—'}
            testid="analytics-plan-used" />
          <StatRow label="Active Kickback limit"
            value={pl.active_kickback_limit ?? 'unlimited'}
            testid="analytics-plan-limit" />
          <StatRow label="Plan synced"
            value={pl.plan_synced ? 'Yes' : 'No'}
            testid="analytics-plan-synced" />
        </SectionCard>

        <SectionCard icon={AlertTriangle} title="Needs Attention"
          testid="analytics-attention-card">
          <StatRow label="Exhausted claim links"
            value={a.claim_links_exhausted}
            testid="analytics-attention-exhausted" />
          <StatRow label="Kickbacks without claim links"
            value={a.portfolios_without_claim_links}
            testid="analytics-attention-no-claims" />
          <StatRow label="Kickbacks without attached utilities"
            value={a.portfolios_without_attached_utilities}
            testid="analytics-attention-no-utilities" />
          <StatRow label="Kickbacks with safe-language flags"
            value={a.portfolios_with_safe_language_flags}
            testid="analytics-attention-safe-portfolios" />
          <StatRow label="Utilities with safe-language flags"
            value={a.utilities_with_safe_language_flags}
            testid="analytics-attention-safe-utilities" />
        </SectionCard>
      </div>

      <div style={{ ...cardStyle, marginTop: 16 }}
        data-testid="analytics-latest-redemptions">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Clock size={18} color={theme.colors.primary} />
          <div style={{ fontWeight: 800, fontSize: 15,
            color: theme.colors.text.primary }}>Latest Redemptions</div>
        </div>
        {(r.latest || []).length === 0 ? (
          <div data-testid="analytics-latest-empty"
            style={{ color: theme.colors.text.muted, fontSize: 13, padding: 8 }}>
            No redemptions yet.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 8 }}>
            {(r.latest || []).map(row => (
              <div key={row.id} data-testid={`analytics-latest-row-${row.id}`}
                style={{ padding: 10, background: theme.colors.bg.secondary,
                  borderRadius: theme.radius.sm, display: 'flex', gap: 10,
                  flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 700, color: theme.colors.text.primary,
                    fontSize: 13 }}>
                    {row.portfolio_name || '—'}
                    {row.claim_title && (
                      <span style={{ color: theme.colors.text.muted,
                        fontWeight: 500, marginLeft: 6 }}>
                        · {row.claim_title}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: theme.colors.text.muted,
                    marginTop: 2 }}>
                    Holder: {row.holder_email || 'anonymous'}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ padding: '2px 8px', borderRadius: 999,
                    background: `${theme.colors.status.success}1A`,
                    color: theme.colors.status.success, fontSize: 10,
                    fontWeight: 800, textTransform: 'uppercase' }}>
                    {row.redemption_status}
                  </span>
                  <div style={{ fontSize: 11, color: theme.colors.text.muted,
                    marginTop: 2 }}>
                    {row.redeemed_at
                      ? new Date(row.redeemed_at).toLocaleString()
                      : '—'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
